import React, { useMemo } from "react";
import { Calendar, Badge, Radio, Space, Tooltip, Row, Col, Typography } from "antd";
import dayjs from "../dayjs";
const { Title } = Typography;

/**
 * ScheduleCalendar component displays a calendar with affected dates based on schedule configuration
 * @param {Object} props
 * @param {Array} props.schedulePeriods - Array of schedule periods
 * @param {Object} props.operatingDays - Operating days configuration
 * @param {Array} props.excludedPeriods - Array of excluded periods
 * @param {Array} props.includedSchedules - Array of included schedules
 * @param {Array} props.excludedSchedules - Array of excluded schedules
 */
export default function ScheduleCalendar({
  schedulePeriods,
  operatingDays,
  excludedPeriods,
  includedSchedules,
  excludedSchedules,
  calendarTitle,
  runningDates
}) {
  // Calculate affected dates for the calendar
  const affectedDates = useMemo(() => {
    if(runningDates?.length) return runningDates;

    const dates = [];
    
    // Add dates from schedule periods
    schedulePeriods.forEach(period => {
      if (period.start && period.end) {
        let current = period.start.clone();
        while (current.isSameOrBefore(period.end)) {
          // Only add dates that match operating days
          if (operatingDays[current.format('dddd').toLowerCase()]) {
            dates.push(current.clone());
          }
          current = current.add(1, 'day');
        }
      }
    });
    
    // Add dates from included schedules
    includedSchedules.forEach(schedule => {
      if (schedule.schedulePeriods) {
        // If it's a plain object with schedulePeriods (like PREDEFINED_SCHEDULES)
        schedule.schedulePeriods.forEach(period => {
          if (period.start && period.end) {
            let current = dayjs(period.start);
            const end = dayjs(period.end);
            
            // Add all dates in the period, regardless of operating days
            while (current.isSameOrBefore(end)) {
              dates.push(current.clone());
              current = current.add(1, 'day');
            }
          }
        });
      } else if (schedule.getRunningDates) {
        // Use the Schedule class's getRunningDates method to get all running dates
        const runningDates = schedule.getRunningDates();
        dates.push(...runningDates);
      }
    });
    
    // Remove excluded dates
    excludedPeriods.forEach(period => {
      if (period.start && period.end) {
        let current = period.start.clone();
        while (current.isSameOrBefore(period.end)) {
          const index = dates.findIndex(d => d.isSame(current, 'day'));
          if (index !== -1) {
            dates.splice(index, 1);
          }
          current = current.add(1, 'day');
        }
      }
    });
    
    // Remove dates from excluded schedules
    excludedSchedules.forEach(schedule => {
      if (schedule.schedulePeriods) {
        // If it's a plain object with schedulePeriods (like PREDEFINED_SCHEDULES)
        schedule.schedulePeriods.forEach(period => {
          if (period.start && period.end) {
            let current = dayjs(period.start);
            const end = dayjs(period.end);
            
            // Remove all dates in the period, regardless of operating days
            while (current.isSameOrBefore(end)) {
              const index = dates.findIndex(d => d.isSame(current, 'day'));
              if (index !== -1) {
                dates.splice(index, 1);
              }
              current = current.add(1, 'day');
            }
          }
        });
      } else if (schedule.getRunningDates) {
        const runningDates = schedule.getRunningDates();
        runningDates.forEach(date => {
          const index = dates.findIndex(d => d.isSame(date, 'day'));
          if (index !== -1) {
            dates.splice(index, 1);
          }
        });
      }
    });

    return dates;
  }, [schedulePeriods, operatingDays, excludedPeriods, includedSchedules, excludedSchedules, runningDates]);

  // Function to render date cells in the calendar
  const dateCellRender = (date) => {
    const isAffected = affectedDates.some(d => d.isSame(date, 'day'));
    return isAffected ? (
      <Tooltip title="Schedule runs on this day">
        <div 
          style={{ 
            width: '100%', 
            height: '100%', 
            backgroundColor: '#52c41a', 
            opacity: 0.3,
            borderRadius: '2px',
            position: 'absolute',
            top: 0,
            left: 0
          }}
        />
      </Tooltip>
    ) : null;
  };

  return (
    <div className="schedule-calendar">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <Title level={5} style={{marginBottom: 0, fontWeight: 500}}>{calendarTitle}</Title>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 16, height: 16, backgroundColor: '#52c41a', opacity: 0.3, marginRight: 8 }}></div>
          <span>Schedule runs on this day</span>
        </div>
      </div>
      <Calendar dateCellRender={dateCellRender} />
    </div>
  );
} 